import { BuilderComponent } from "@builder.io/react";
import {
  GetContentOptions,
  UserAttributes,
} from "@builder.io/sdk/dist/src/builder.class";
import { initializeBuilder } from "@faire/builder-core/BuilderComponents/initializeBuilder";
import { useBuilderContentQuery } from "@faire/builder-core/BuilderComponents/useBuilderContentQuery";
import { LoadingSkeleton } from "@faire/web/slate/LoadingSkeleton";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React, { PropsWithChildren, Suspense, useEffect } from "react";

// eslint-disable-next-line import/no-unassigned-import
import "../globals.css";

import { getBuilderAPIKey } from "./builderUtils";

interface IProps extends PropsWithChildren {
  modelName: string;
  onViewEvent?: () => void;
  data?: object;
  targets?: UserAttributes;
  contentOptions?: GetContentOptions;
  fullWidth?: boolean;
  /**
   * Used to pass callabcks down to the Builder component to use in the UIs (e.g. actions to trigger for instance on click of a button).
   * *CAUTION* All callbacks passed will not be observed for changes.
   * Context is available in actions and bindings as context.*, such as context.myFunction().
   * See https://www.builder.io/c/docs/custom-actions#example-setting-up-a-custom-action-on-a-button for more information
   * @example
   * context={{
   *   myFunction: () => alert('Triggered!'),
   * }}
   */
  context?: Record<string, any>;
}

const LazyRenderWithBuilder: React.FC<IProps> = ({
  modelName,
  onViewEvent,
  data,
  targets,
  contentOptions,
  fullWidth,
  context,
}) => {
  const apiKey = getBuilderAPIKey();

  useEffect(() => {
    initializeBuilder(apiKey);
  }, [apiKey]);

  const { data: sectionContent, isPending } = useBuilderContentQuery(
    modelName,
    {
      userAttributes: targets,
      ...contentOptions,
    }
  );
  const ref = useDoActionWhenVisible(onViewEvent);

  if (isPending) {
    return <LoadingSkeleton aspectRatio="16/9" width="100%" />;
  }
  return (
    <div
      ref={ref}
      className="builder-io-content"
      style={fullWidth ? { width: "100%" } : undefined}
    >
      <BuilderComponent
        content={sectionContent}
        model={modelName}
        data={data}
        context={context}
      />
    </div>
  );
};

export const RenderWithBuilder: React.FC<IProps> = (props) => {
  return (
    <Suspense fallback={null}>
      {/* carousel and other widets are lazy loaded, so we need suspense  */}
      <LazyRenderWithBuilder {...props} />
    </Suspense>
  );
};
